/**
 * Export a screen: which rows, which columns, and in what text. Any screen —
 * an index, a filter, a list. `docs/features/tracklists.md`, *Import, paste,
 * export*.
 *
 * Rows come from the grid (`ScreenerExporter`), after its filter and sort,
 * because that is what is on screen and the grid is what applied both.
 *
 * Three shapes:
 *
 * - **symbol** — one per line. On the clipboard that is a block the Add row and
 *   Import both read back unchanged; in a file it gets a `Symbol` header.
 * - **price** — `Symbol, Price`. On a list screen the price is the member's
 *   *added at* figure, the one number a list owns; elsewhere it is the row's
 *   last price. Blank when there is none, never zero.
 * - **all** — every column the grid shows, as the grid formats it.
 */

import type { ScreenerExporter } from "@qufolio/screener-grid";

import type { TrackList } from "./track-lists";

export type ExportShape = "symbol" | "price" | "all";
export type ExportTarget = "clipboard" | "csv";

/** The fields a row's last price may be under, in the order `closesFor` tries them. */
const PRICE_FIELDS = ["close", "last", "price"] as const;

/** A CSV cell, quoted only when it has to be. */
export function csvCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function symbolsOf(exporter: ScreenerExporter): { symbol: string; row: Record<string, unknown> }[] {
  return exporter
    .rows()
    .map((row) => ({ symbol: String(row["symbol"] ?? ""), row: row as Record<string, unknown> }))
    .filter((entry) => entry.symbol !== "");
}

function priceOf(row: Record<string, unknown>): number | null {
  for (const field of PRICE_FIELDS) {
    const value = row[field];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}

export function exportText(
  exporter: ScreenerExporter,
  { shape, target, list }: { shape: ExportShape; target: ExportTarget; list?: TrackList },
): string {
  if (shape === "all") return exporter.csv();

  const rows = symbolsOf(exporter);
  if (shape === "symbol") {
    const lines = rows.map((entry) => entry.symbol);
    return (target === "csv" ? ["Symbol", ...lines] : lines).join("\n");
  }

  const added = new Map(list?.members.map((member) => [member.symbol.toUpperCase(), member.addedClose]));
  const lines = rows.map(({ symbol, row }) => {
    const price = list ? (added.get(symbol.toUpperCase()) ?? null) : priceOf(row);
    return `${csvCell(symbol)},${price === null ? "" : price}`;
  });
  return [list ? "Symbol,Added at" : "Symbol,Price", ...lines].join("\n");
}

/** How many symbols an export will carry — the dialog's title count. */
export function exportCount(exporter: ScreenerExporter): number {
  return symbolsOf(exporter).length;
}

/** `Weekend Buys` → `Weekend-Buys-2026-10-18.csv`. */
export function exportFileName(title: string, now: Date = new Date()): string {
  const stem = title.trim().replace(/[^\w.-]+/g, "-").replace(/^-+|-+$/g, "") || "screen";
  const day = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return `${stem}-${day}.csv`;
}
